/**
 * Shell copy for `src/features` (keys: `shell.features.<file>.<key>`).
 *
 * `fr` is the source; `en` and `ar` are typed against it, so a missing or extra
 * key fails `tsc` (and parity.test.ts). Keys are referenced from the route files
 * through `t()` / `tr()` — edit values freely, never rename a key without its call site.
 *
 * Locale file: en (typed against ./fr). One file per locale so
 * every non-default locale is a separate, lazily-loaded chunk.
 */

import type { Catalog } from './fr';

const en: Catalog = {
  advisorAdvisorPanel: {
    important: 'Important',
    suggested: 'Suggested',
    info: 'Info',
    unexpectedError: 'Unexpected error',
    analysisConfidenceIndex: 'Analysis confidence index',
    confidencePct: 'Confidence {{pct}}%',
    applyTheRecommendation: 'Apply the recommendation',
    titleLabelThisActionChangesYour: '{{title}} — “{{label}}”? This action changes your data.',
    actionAppliedSuccessfully: 'Action applied successfully.',
    inProgress: 'In progress…',
    recommendedSlot: 'Recommended slot',
    analysisRecommendations: 'Analysis & recommendations',
    aiAnalysisAndRecommendations: 'AI analysis and recommendations',
    analyzing: 'Analyzing…',
    refresh: 'Refresh',
    analysisInProgress: 'Analysis in progress…',
    aiRecommendationsWillBeAvailableSoon: 'AI recommendations will be available soon for this section.',
    couldNotLoadTheRecommendationsMessage: 'Could not load the recommendations: {{message}}',
    verdictVerdict: 'Verdict: {{verdict}}',
    bestTimesToPublish: '🕒 Best times to publish',
    apply: 'Apply',
    trendingTopics: '🔥 Trending topics',
    suggestedThemesFormats: '🎯 Suggested themes & formats',
    noRecommendationForNowNotEnough: 'No recommendation for now — not enough data to analyze. Come back after a few posts or campaigns.',
  },
  composerViralityScore: {
    estimatedViralityScore: 'Estimated virality score',
    viralityPotential: 'Virality potential',
    computing: 'computing…',
    indicativeNeverBlocksPublishing: 'Indicative — never blocks publishing.',
    slotHoursToNearestSlotH: 'Slot {{hours_to_nearest_slot}} h from the best time to publish.',
  },
  copilotActionExecutor: {
    theCopilotIsWaitingForYour: 'The copilot is waiting for your confirmation to continue.',
    importNotPossibleUnknownEntityV: 'Import not possible: unknown entity ({{v}}) and no endpoint provided',
    noRecordToImport: 'No record to import',
    lengthRecordSImportedViaEndpoint: '{{length}} record(s) imported via {{endpoint}}',
    importFailedEndpoint: 'Import failed ({{endpoint}})',
    importFailedEndpointV: 'Import failed ({{endpoint}}){{v}}',
    okRecordSImportedViaEndpoint: '{{ok}} record(s) imported via {{endpoint}}',
    failedFailed: ' ({{failed}} failed)',
    toolExecutedToolname: 'Tool executed: {{toolName}}',
    toolToolnameFailed: 'Tool {{toolName}} failed',
    targetRouteMissing: 'target.route missing',
    navigatingToRoute: 'Navigating to {{route}}',
    targetFieldMissing: 'target.field missing',
    copilotNotification: 'Copilot notification',
    toastMessage: 'Toast: {{message}}',
    confirmationHandledByTheInterface: 'Confirmation handled by the interface',
    unknownActionType: 'Unknown action type',
  },
  copilotCopilotWidget: {
    yes: 'yes',
    no: 'no',
    navigation: 'Navigation',
    openAForm: 'Open a form',
    fillInAField: 'Fill in a field',
    fillInAForm: 'Fill in a form',
    submitAForm: 'Submit a form',
    selectRows: 'Select rows',
    highlight: 'Highlight',
    openAWindow: 'Open a window',
    scrollTo: 'Scroll to',
    notification: 'Notification',
    importRecords: 'Import records',
    waitForAConfirmation: 'Wait for a confirmation',
    runAnAction: 'Run an action',
    executed: 'executed',
    failed: 'failed',
    ignored: 'ignored',
    ready: 'Ready',
    reading: 'Reading…',
    paused: 'Paused',
    awaitingYourConfirmation: 'Awaiting your confirmation',
    done: 'Done',
    pending: 'pending',
    inProgress: 'in progress',
    pausedConfirmation: 'paused: confirmation',
    noValueEntered: '(no value entered)',
    submitTheFormidForm: 'Submit the “{{formId}}” form',
    runTheToolAction: 'Run the “{{tool}}” action',
    submitTheForm: 'Submit the form',
    headToolnoteWithTheFollowingValues: '{{head}}{{toolNote}} with the following values:\n{{rendered}}',
    proposedActionS: 'proposed action(s)',
    stopReadingThisAnswer: 'Stop reading this answer',
    readThisAnswerAloud: 'Read this answer aloud',
    stopReading: 'Stop reading',
    readTheAnswerAloud: 'Read the answer aloud',
    stop: '⏹ Stop',
    read: '🔊 Read',
    preview: 'preview',
    confirmTheActionLabel: 'Confirm the action: {{label}}',
    confirm: 'Confirm',
    run: 'Run',
    rejectTheActionLabel: 'Reject the action: {{label}}',
    reject: 'Reject',
    openTheCopilot: 'Open the copilot',
    lengthActionSPending: '{{length}} action(s) pending',
    copilotBackendUnavailable: 'Copilot backend unavailable',
    copilot: 'Copilot',
    copilotBackendConnected: 'Copilot backend connected',
    agent: 'Agent',
    assistant: 'Assistant',
    readAnswersAloud: 'Read answers aloud',
    readingAnswersAloudEnabledDisable: 'Reading answers aloud enabled — disable',
    readAnswersAloudAutomatically: 'Read answers aloud automatically',
    voice: 'Voice',
    advancedMode: 'Advanced mode',
    hideAdvancedOptions: 'Hide advanced options',
    showAdvancedOptions: 'Show advanced options',
    advanced: 'Advanced',
    disableStepByStepMode: 'Disable step-by-step mode',
    enableStepByStepMode: 'Enable step-by-step mode',
    observableExecutionPlayPauseStepBy: 'Observable execution: play, pause, step by step and intervention',
    stepByStep: 'Step-by-step',
    switchToAssistantMode: 'Switch to assistant mode',
    switchToAgentMode: 'Switch to agent mode',
    assistantMode: 'Assistant mode',
    agentMode: 'Agent mode',
    minimizeTheCopilot: 'Minimize the copilot',
    copilotBackendUnavailableLocalModeSimula: 'Copilot backend unavailable — local mode, simulated actions marked “preview”.',
    advancedOptions: 'Advanced options',
    model: 'Model',
    temperatureTemperature: 'Temperature ({{temperature}})',
    temperature: 'Temperature',
    maxStepsAgent: 'Max steps (agent)',
    maximumNumberOfAgentSteps: 'Maximum number of agent steps',
    sentToTheCopilotIgnoredAs: 'Sent to the copilot; ignored as long as the backend does not use them.',
    copilotMessages: 'Copilot messages',
    askAQuestionDropAFile: 'Ask a question, drop a file or ask the copilot for an action.',
    theCopilotIsThinking: 'The copilot is thinking…',
    dropTheFileToAnalyzeIt: 'Drop the file to analyze it',
    stepByStepPlayer: 'Step-by-step player',
    pausePlayback: 'Pause playback',
    startPlayback: 'Start playback',
    pause: 'Pause',
    read2: 'Read',
    runTheNextStep: 'Run the next step',
    nextStep: 'Next step',
    stopStepByStepPlayback: 'Stop step-by-step playback',
    stop2: 'Stop',
    playbackSpeed: 'Playback speed',
    copilotPlaybackSpeed: 'Copilot playback speed',
    slow: 'Slow',
    normal: 'Normal',
    fast: 'Fast',
    continueExecution: 'Continue execution',
    continue: 'Continue',
    changeWhatComesNextViaA: 'Change what comes next via a message to the copilot',
    edit: 'Edit',
    askTheCopilotAQuestion: 'Ask the copilot a question',
    askAQuestion: 'Ask a question',
    stepByStepExecutionQueue: 'Step-by-step execution queue',
    proposedActions: 'Proposed actions',
    showOrHideTheTimelineOf: 'Show or hide the timeline of executed actions',
    actionTimelineLength: 'Action timeline ({{length}})',
    executedActions: 'Executed actions',
    noActionExecutedYet: 'No action executed yet.',
    remainingAiCreditBalanceForThis: 'Remaining AI credit balance for this account',
    credit: 'credit',
    left: 'left',
    attachAFile: 'Attach a file',
    attachAFileToAnalyze: 'Attach a file to analyze',
    fileImportUnavailableCopilotBackendOffli: 'File import unavailable — copilot backend offline',
    askForAnAction: 'Ask for an action…',
    askAQuestion2: 'Ask a question…',
    messageForTheCopilot: 'Message for the copilot',
    intervenePauseStepByStepPlayback: 'Intervene: pause step-by-step playback and send this message',
    pausesStepByStepPlaybackThen: 'Pauses step-by-step playback, then sends your message',
    intervene: 'Intervene',
    sendTheMessage: 'Send the message',
    send: 'Send',
    closeTheNotification: 'Close the notification',
  },
  copilotFileUpload: {
    fileTooLargeVMbLimit: 'File too large ({{v}} MB). Limit: {{max_upload_mb}} MB.',
    attachedFileName: 'Attached file: {{name}}',
    fileTooLargeName: 'File too large: “{{name}}”.',
    extractionUnavailableTheCopilotBackendCo: 'Extraction unavailable — the copilot backend could not process “{{name}}”.',
  },
  copilotPageEffects: {
    fieldsFilledMissing: '{{filled}} field(s) filled, {{missing}} not found',
    fieldsFilled: '{{filled}} field(s) filled',
    domUnavailable: 'DOM unavailable',
    lengthFieldSFilled: '{{length}} field(s) filled',
    noFieldProvided: 'No field provided',
    noTargetFieldFoundV: 'No target field found ({{v}})',
    item: 'item',
    noElementToHighlight: 'No element to highlight',
    highlightingV: 'Highlighting: {{v}}',
    noElementToScrollTo: 'No element to scroll to',
    scrollingToV: 'Scrolling to {{v}}',
    openingTheVWindow: 'Opening the {{v}} window',
    openingTheModalidWindow: 'Opening the {{modalId}} window',
    noWindowOpenerRegistered: 'No window opener registered',
    lengthRowSSelected: '{{length}} row(s) selected',
    noRowToSelect: 'No row to select',
    noTargetRowFound: 'No target row found',
    selectedRowSSelected: '{{selected}} row(s) selected',
    formVSubmitted: 'Form {{v}} submitted',
    noFormToSubmitSubmitformHandler: 'No form to submit (submitForm handler required)',
    openingTheVForm: 'Opening the {{v}} form',
    openingTheFidForm: 'Opening the {{fid}} form',
    noFormOpenerRegistered: 'No form opener registered',
  },
  copilotRichRegistry: {
    reopen: 'Reopen “',
    theWindow: 'the window',
    tabV: 'Tab {{v}}',
    tabs: 'Tabs',
  },
  copilotRichRichBlocks: {
    yes: 'Yes',
    no: 'No',
    noData: 'No data.',
    pagePageLengthOfTotalResults: 'Page {{page}} · {{length}} of {{total}} results',
    send: 'Send',
    sent: 'Sent ✓',
  },
  copilotUseVoiceRecorder: {
    audioRecordingIsNotSupportedBy: 'Audio recording is not supported by this browser.',
    microphoneAccessDeniedAllowTheMicrophone: 'Microphone access denied. Allow the microphone in your browser settings to use voice input.',
    couldNotInitializeTheAudioRecorder: 'Could not initialize the audio recorder in this browser.',
    anErrorOccurredDuringAudioRecording: 'An error occurred during audio recording.',
  },
  copilotVoiceRecorder: {
    theVoiceServiceIsTemporarilyUnavailable: 'The voice service is temporarily unavailable. Try again later.',
    noSoundCapturedTryAgainSpeaking: 'No sound captured — try again speaking closer to the microphone.',
    speechRecognitionTemporarilyUnavailableT: 'Speech recognition temporarily unavailable. Try again later.',
    theTranscriptionFailedTryAgain: 'The transcription failed. Try again.',
    accessDeniedInsufficientRightsToDictate: 'Access denied — insufficient rights to dictate.',
    checkingAvailability: 'Checking availability…',
    speechRecognitionNotConfigured: 'Speech recognition not configured',
    audioRecordingNotSupportedByThis: 'Audio recording not supported by this browser',
    stopRecordingV: 'Stop recording ({{v}})',
    transcribing: 'Transcribing…',
    voiceInput: 'Voice input',
    stopVoiceRecordingVElapsed: 'Stop voice recording, {{v}} elapsed',
    cancelTheRecording: 'Cancel the recording',
    cancel: 'Cancel',
    transcriptionInProgress: 'Transcription in progress',
    startVoiceInput: 'Start voice input',
  },
  creditsUsage: {
    costMany: '~{{n}} credits',
    costOne: '~{{n}} credit',
    costTitle: 'Estimated cost: {{n}} credits — charged only if the action succeeds',
    costFree: 'Included',
    usedToast: '{{charged}} credits used · balance {{balance}}',
    usedToastOne: '{{charged}} credit used · balance {{balance}}',
    usedToastNoBalance: '{{charged}} credits used',
    insufficientCost: 'Cost: {{cost}} cr · balance: {{balance}} cr',
    recharge: 'Top up',
  },
  creditsAiCreditsBadge: {
    aiCreditsUnlimitedSuperadminAccount: 'AI credits — unlimited (superadmin account)',
    unlimited: 'unlimited',
    aiCreditsLowBalanceClickTo: 'AI credits — low balance, click to top up',
    aiCreditsAvailableAvailable: 'AI credits available: {{available}}',
    pctusedUsedThisCycle: '{{pctUsed}}% used this cycle',
    resetsOnResetlabel: 'resets on {{resetLabel}}',
    cr: 'cr',
    topUp: 'Top up',
  },
  creditsCreditError: {
    insufficientAiCreditsTopUpYour: 'Insufficient AI credits — top up your balance (Credits badge, header) to try again.',
  },
  creditsCreditGuardHost: {
    creditsToppedUpYourPausedTasks: 'Credits topped up — your paused tasks resume automatically.',
    personalAiCapReachedContactAn: 'Personal AI cap reached — contact an administrator',
    aiCreditsTemporarilyUnavailableTryAgain: 'AI credits temporarily unavailable — try again',
    insufficientAiCreditsTopUpYour: 'Insufficient AI credits — top up your balance',
    lowAiCreditBalanceConsiderTopping: 'Low AI credit balance — consider topping up your wallet.',
    requestSentToAnAdministrator: 'Request sent to an administrator.',
    couldNotSendTheRequestRight: 'Could not send the request right now.',
    personalAiCapReached: 'Personal AI cap reached',
    aiCreditsTemporarilyUnavailable: 'AI credits temporarily unavailable',
    insufficientAiCredits: 'Insufficient AI credits',
    later: 'Later',
    askAnAdmin: 'Ask an admin',
    iHaveACoupon: 'I have a coupon',
    topUpAndContinue: 'Top up and continue',
    topUpMyCredits: 'Top up my credits',
    yourAiCreditBalanceDoesNot: 'Your AI credit balance does not cover this operation.',
    youHaveReachedYourPersonalMonthly: 'You have reached your personal monthly cap',
    vCr: ' ({{v}} cr)',
    theOrganizationSWalletStillHas: '. The organization\'s wallet still has credits: ask an administrator to raise your cap (no need to top up).',
    theBalanceCouldNotBeVerified: 'The balance could not be verified right now (this is not an empty balance). Try again in a moment.',
    toContinue: 'To continue',
    thisAiAction: 'this AI action',
    youNeedToTopUpYour: ', you need to top up your AI credit wallet.',
    required: 'Required',
    available: 'Available',
    missing: 'Missing',
    noCreditWasDebitedTheAi: 'No credit was debited — the AI call was not executed.',
    afterTheTopUpYourAction: ' After the top-up, your action will resume automatically.',
    topUpToTryAgain: ' Top up to try again.',
  },
  creditsFeatureLabels: {
    crmSummary: 'CRM summary',
    textABTest: 'Text A/B test',
    accountIntelligence: 'Account intelligence',
    communityAi: 'Community AI',
    contentGeneration: 'Content generation',
    themeGeneration: 'Theme generation',
    titleGeneration: 'Title generation',
    descriptionGeneration: 'Description generation',
    contentWriting: 'Content writing',
    contentStudio: 'Content studio',
    hashtagGeneration: 'Hashtag generation',
    imagePrompts: 'Image prompts',
    ghostwriterWriting: 'Ghostwriter — writing',
    ghostwriterRewriting: 'Ghostwriter — rewriting',
    contentImprovement: 'Content improvement',
    agentExecution: 'Agent execution',
    planningUnderstanding: 'Planning (understanding)',
    publicationReview: 'Publication review',
    aiReviewPanel: 'AI review panel',
    aiProspectingResearch: 'AI prospecting research',
    prospectingCalendar: 'Prospecting calendar',
    prospectingStrategy: 'Prospecting strategy',
    replyAssistant: 'Reply assistant',
    contentRepurposing: 'Content repurposing',
    aiAssistantChat: 'AI assistant (chat)',
    aiSuggestions: 'AI suggestions',
    test: 'Test',
    copilot: 'Copilot',
    agentPersonaTask: 'Agent — persona task',
    extractionMemory: 'Extraction (memory)',
    ragEmbeddings: 'RAG embeddings',
    visualAnalysisAnalytics: 'Visual analysis (analytics)',
    studioGeneration: 'Studio — generation',
    carousel: 'Carousel',
    thread: 'Thread',
    generation: 'Generation',
    workflow: 'Workflow',
    sdrAutopilot: 'SDR autopilot',
    aiCallAgent: 'AI call (agent)',
    semanticSearch: 'Semantic search',
    ragSearchEmbeddings: 'RAG search (embeddings)',
    aiTeamExecution: 'AI team — execution',
    aiTeamPlanning: 'AI team — planning',
    aiTeamComposition: 'AI team — composition',
    aiTeamMission: 'AI team — mission',
    conversationAnalysis: 'Conversation analysis',
    prospecting: 'Prospecting',
    content: 'Content',
    studio: 'Studio',
    ghostwriter: 'Ghostwriter',
    community: 'Community',
    inbox: 'Inbox',
    ai: 'AI',
    aiAssistant: 'AI assistant',
    aiTeam: 'AI team',
    memory: 'Memory',
    analytics: 'Analytics',
    workflows: 'Workflows',
    aiAgents: 'AI / agents',
    replyDraftConversations: 'Reply draft (conversations)',
    replyRewriteConversations: 'Reply rewrite (conversations)',
    communityRulesSummary: 'Community rules summary',
    listening: 'Listening',
    advisorContent: 'AI advisor — content',
    advisorAds: 'AI advisor — ads',
    advisorProspection: 'AI advisor — prospecting',
    advisorCampaign: 'AI advisor — campaign',
    copilotTurn: 'Copilot — turn',
    copilotTranscribe: 'Voice transcription',
    assistantDo: 'AI assistant — action',
    launchPlan: 'Project launch plan',
    buildInPublic: 'Build in public — drafts',
    memoryExtraction: 'Memory extraction',
    ragDocumentUpload: 'Document upload (RAG)',
    ragUrlIngest: 'URL ingest (RAG)',
    contentIdeas: 'Content ideas',
    humanizeContent: 'Text humanizing',
    plagiarismCheck: 'Plagiarism check',
    postReviewLoop: 'Multi-expert review loop',
    generateCalendar: 'Calendar generation',
    contentPerformanceLearn: 'Performance learning',
    contactEnrich: 'Contact enrichment',
    companyEnrich: 'Company enrichment',
    prospectDiscovery: 'Prospect discovery',
    prospectEnrich: 'Prospect enrichment',
    b2bContactEnrich: 'B2B contact enrichment',
    b2bVisitorIdentify: 'Visitor identification',
    b2bJobChangeScan: 'Job-change scan',
    apolloContactEnrich: 'Apollo — contact enrichment',
    apolloCompanyEnrich: 'Apollo — company enrichment',
    apolloProspectDiscovery: 'Apollo — prospect discovery',
    apolloB2bContactEnrich: 'Apollo — B2B enrichment',
    socialTargetDiscovery: 'Social target discovery',
    callInsight: 'Call insights',
    crmJourneyAdvise: 'CRM journey advice',
    crmScoringRecommend: 'Scoring recommendation',
    emailOrganize: 'AI e-mail organizing',
    growthRecommendations: 'Growth recommendations',
    programInviteEmail: 'Partner invitation e-mail',
    lifecycleEmail: 'Lifecycle e-mail',
    imageGeneration: 'Image generation',
    videoGeneration: 'Video generation',
    imageToVideo: 'Image to video',
    avatarGeneration: 'Avatar generation',
    productBriefExtract: 'Product brief — extraction',
    productBriefCompetitors: 'Product brief — competitors',
    productBriefSeed: 'Product brief — strategy',
    dailyMovesRegenerate: 'Daily moves — regenerate',
    strategyCheckin: 'Weekly strategy check-in',
    launchKitGenerate: 'Launch kit',
    launchKitDirectoryCopy: 'Launch kit — directory copy',
    autopilotContentChannel: 'Autopilot — content channel',
    autopilotEmailSequence: 'Autopilot — e-mail sequence',
    catchCapture: 'Conversation capture',
    creativeAdRun: 'AI ad creative',
    ghostwriterAdCopy: 'Ghostwriter — ad copy',
    ghostwriterTranslate: 'Ghostwriter — translation',
    learningTranspose: 'Learning transpose',
    marketingStrategyDraft: 'Marketing strategy draft',
    sourceStudioIngest: 'Source studio — ingest',
    sourceStudioGenerate: 'Source studio — generate',
    sourceStudioChat: 'Source studio — chat',
    sourceStudioAudio: 'Source studio — audio overview',
    translateLocalize: 'Translation / localization',
    siteAudit: 'Site audit',
    analyticsSiteAudit: 'Site audit — AI analysis',
    analyticsSentimentAnalyze: 'Sentiment analysis',
    analyticsTrendsDetect: 'Trend detection',
    analyticsCompetitorCompare: 'Competitor comparison',
    analyticsCrmMl: 'CRM ML analysis',
    analyticsSubjectSummary: 'Subject summary',
    analyticsReportNarrative: 'Report narrative',
    analyticsGeo: 'GEO analysis',
    contentImageGenerate: 'Image generation',
    contentAiFillTemplate: 'Design — AI fill',
    contentAiSuggestPalette: 'Design — AI palette',
    contentVideoGenerate: 'Video generation',
    contentVideoTranscribe: 'Video transcription',
    contentVideoRepurpose: 'AI video repurpose',
    contentAudioVoiceover: 'Voice-over',
    contentAudioVoiceClone: 'Cloned voice',
    contentAudioVoiceEnroll: 'Voice enrollment',
    contentVideoAvatar: 'Avatar video',
    contentImageRemoveBackground: 'Background removal',
    contentImageUpscale: 'Image upscale',
    contentImageCleanup: 'Image cleanup',
    contentImageInpaint: 'Image inpainting',
    contentTranslateLocale: 'Content translation',
    contentCarouselAiSlide: 'Carousel — AI slide',
    contentCarouselCopyGenerate: 'Carousel — copy',
    contentInfographicGenerate: 'Infographic generation',
    contentAnimationGenerate: 'Animation generation',
    contentPostPackGenerate: 'Post pack',
    contentVideoScriptGenerate: 'Video script',
    contentLongformOutline: 'Long-form — outline',
    contentLongformGenerate: 'Long-form — writing',
    contentLongformSection: 'Long-form — section',
    contentSeoSuggest: 'SEO suggestions',
    contentGeoSuggest: 'GEO suggestions',
  },
  creditsModelPicker: {
    vV2CrPer1kTokens: '{{v}}/{{v2}} cr per 1k tokens (in/out)',
    model: 'Model',
    auto: 'Auto',
    modelChosenAutomaticallyBestChoice: 'Model chosen automatically (best choice)',
    autoRecommended: 'Auto (recommended)',
    labelVCr1kOut: '· {{label}} — {{v}} cr/1k out',
    modelChosenAutomaticallyForTheTask: 'Model chosen automatically for the task.',
    specificModel: 'Specific model.',
  },
  creditsPausedRunsPanel: {
    copilotConversation: 'Copilot conversation',
    multiAgentPlan: 'Multi-agent plan',
    multiAgentExecution: 'Multi-agent execution',
    agentTask: 'Agent task',
    workflow: 'Workflow',
    sdrProspecting: 'SDR prospecting',
    resumeStarted: 'Resume started.',
    creditsStillInsufficientTopUpMore: 'Credits still insufficient — top up more.',
    couldNotResumeThisTaskRight: 'Could not resume this task right now.',
    pausedTasksCreditsExhausted: 'Paused tasks (credits exhausted)',
    refresh: 'Refresh',
    theseRunsWerePausedForLack: 'These runs were paused for lack of credits. They resume automatically after a top-up — or resume them manually here.',
    paused: 'paused',
    blocked: 'blocked',
    resuming: 'Resuming…',
    resume: 'Resume',
  },
  demoDemoRecorder: {
    yourBrowserDoesNotSupportScreen: 'Your browser does not support screen recording, or permission was denied.',
    errorDuringRecordingTryAgain: 'Error during recording. Try again.',
    couldNotGenerateTheVideo: 'Could not generate the video.',
  },
  demoDemoTour: {
    dashboard: 'Dashboard',
    overviewOfYourActivityContentEngagement: 'Overview of your activity: content, engagement, ads, tasks and shortcuts.',
    aiRecommendations: 'AI recommendations',
    yourNextBestActionsPrioritizedBy: 'Your next best actions prioritized by AI: what to publish, whom to follow up, which ad to adjust — every tip applicable in one click.',
    marketingStrategy: 'Marketing strategy',
    fromASimpleBriefTheAi: 'From a simple brief, the AI writes positioning, pillars, channels and SMART objectives, then a dated GTM plan and a dashboard of measurable objectives.',
    projectsCollaboration: 'Projects & Collaboration',
    aiLaunchPlanChecklistTeamAnd: 'AI launch plan, checklist, team and granular access sharing.',
    content: 'Content',
    createEditAndGetYourPosts: 'Create, edit and get your posts approved (HIL workflow, draft first).',
    newContent: 'New content',
    composeAMultiNetworkPostThe: 'Compose a multi-network post: the editor adapts the text per platform, adds media and preview, then saves as a draft before approval.',
    ideaFeed: 'Idea feed',
    captureAnIdeaOrPasteA: 'Capture an idea or paste a URL: the AI adds it to a feed ranked by potential, ready to turn into a post.',
    approvalsWorkflow: 'Approvals & workflow',
    configureTheMultiStepApprovalChain: 'Configure the multi-step approval chain (reviewers, roles) with Slack/Teams notifications — nothing is published without approval.',
    mediaLibrary: 'Media library',
    yourCentralDamEditableImagesVideos: 'Your central DAM: editable images, videos and diagrams, reusable across all studios and composers.',
    richContentStudio: 'Rich content studio',
    aiWritingHooksHashtagsViralityScore: 'AI writing: hooks, hashtags, virality score, and AI image/video generation.',
    contentLevelRegeneration: 'Content level & regeneration',
    openAPostFromTheCalendar: 'Open a post from the calendar: edit the body text, choose a content level (Beginner → Master), run “Regenerate with AI”, and add an article\'s context to enrich the text automatically.',
    visualStyleGallery: 'Visual style gallery',
    inThePostChooseAVisual: 'In the post, “🎨 Choose a visual style” opens 30 templates sorted by category (Architecture, Diagram, Comparison, Progression, Poster). “Generate an image (by level)” then produces a visual suited to the chosen content level.',
    diagramEditorDrawIo: 'Diagram editor (draw.io)',
    createEditADiagramOpensThe: '“📊 Create / edit a diagram” opens the Diagram Studio: a full draw.io editor. Diagrams are saved to the media library as editable PNG (the mxGraph XML is embedded) and stay editable at any time.',
    exactDiagramRenderedTemplate: 'Exact diagram (rendered template)',
    forAPreciseReproducibleChartDeterministi: 'For a precise, reproducible chart, deterministic rendering generates an exact diagram from a template — consistent visuals from one post to the next, without manual touch-ups.',
    sourceStudioNotebooklmStyle: 'Source Studio (NotebookLM-style)',
    ingestYourSourcesDocumentsLinksNotes: 'Ingest your sources (documents, links, notes), then automatically generate a briefing, a guide, a FAQ, a timeline and a mind map — with a chat over your sources and a podcast script. (Available once the module is deployed.)',
    unifiedStudioAutoRepurpose: 'Unified studio & auto-repurpose',
    oneIdeaTheWholePackPost: 'One idea → the whole pack: post, carousel, infographic, GIF and reel generated at once, then automatically adapted for each network. (Available once the module is deployed.)',
    aiMediaStudio: 'AI media studio',
    generativeEditingEraseOrReplaceAn: 'Generative editing: erase or replace an area with the brush (inpaint), then localize a caption into variants per language and per network in one click.',
    videoAudioStudio: 'Video & audio studio',
    transcriptionReShareableSubtitledClipsTt: 'Transcription, re-shareable subtitled clips, TTS voiceover, cloned voice, avatar and reels — video end to end.',
    carouselEditor: 'Carousel editor',
    buildACarouselSlideBySlide: 'Build a carousel slide by slide and export it as PNG/PDF ready to publish on LinkedIn and Instagram.',
    designCarousels: 'Design & Carousels',
    designAndCarouselEditorForReady: 'Design and carousel editor for ready-to-publish visuals.',
    brandKits: 'Brand kits',
    centralizeLogosColorsAndFontsEvery: 'Centralize logos, colors and fonts: every generated visual automatically follows your brand guidelines.',
    linkInBioFunnels: 'Link-in-bio & Funnels',
    blockBasedPublicPagesHeroForm: 'Block-based public pages (hero, form, scored quiz, product, testimonial, countdown), popups and templates (lead magnet, product, webinar) — lead capture with consent.',
    calendar: 'Calendar',
    scheduleYourMultiNetworkPostsAnd: 'Schedule your multi-network posts and track your programming.',
    generateACalendar: 'Generate a calendar',
    n4StepAssistantDescribeTheMode: '4-step assistant: describe the mode and your inputs, choose a template, preview as a dry-run, then generate a full editorial calendar.',
    linkedinTemplates: 'LinkedIn templates',
    libraryOfPostTemplatesRankedBy: 'Library of post templates ranked by performance — start again from the format that converts best.',
    aiReviewWorkshop: 'AI review workshop',
    agenticReviewLoopTheAiProposes: 'Agentic review loop: the AI proposes, you decide, and every change feeds the content engine\'s learning.',
    publishingMediaStudio: 'Publishing & Media studio',
    contentQueueBulkSchedulingGroupedMedia: 'Content queue, bulk scheduling, grouped Media studio (multiple upload → post/story/reel/boost) and statistics.',
    evergreenRssQueue: 'Evergreen & RSS queue',
    recyclingQueueContentCategoriesAndRss: 'Recycling queue, content categories and RSS auto-publishing to keep a steady rhythm effortlessly.',
    bulkScheduling: 'Bulk scheduling',
    importACsvAndScheduleDozens: 'Import a CSV and schedule dozens of posts at once, each adapted to its network.',
    bulkMediaStudio: 'Bulk media studio',
    uploadSeveralMediaFilesThenSchedule: 'Upload several media files, then schedule them all at once as post, story, reel or carousel on each platform.',
    aiPublishingAssistant: 'AI publishing assistant',
    stepByStepGuidedCreationTheme: 'Step-by-step guided creation (theme → title → description → detail) for a polished post in minutes.',
    publishingHistory: 'Publishing history',
    trackEveryPostSentItsStatus: 'Track every post sent, its status per network and its performance — with replay on failure.',
    platforms: 'Platforms',
    connectFacebookInstagramTiktokYoutubeLin: 'Connect Facebook, Instagram, TikTok, YouTube, LinkedIn, X… and manage your accounts (automatic token reconnection).',
    socialInbox: 'Social inbox',
    unifiedInboxSavedRepliesAndSla: 'Unified inbox, saved replies and SLA rules for engagement.',
    inboxAutomation: 'Inbox · Automation',
    moderationRulesEngagementHoursAndRe: 'Moderation rules, engagement hours and re-engagement policies to handle comments and messages without constant supervision.',
    aiCommunityManagement: 'AI community management',
    commentDashboardAssistedRepliesAndEditor: 'Comment dashboard, assisted replies and editorial profiles — engage your community at scale.',
    repliesMacros: 'Replies & macros',
    libraryOfReadyMadeRepliesAnd: 'Library of ready-made replies and macros to answer quickly in a consistent voice.',
    slaSatisfaction: 'SLA & satisfaction',
    responseTimePoliciesAndCsatSurveys: 'Response-time policies and CSAT surveys to guarantee measurable service.',
    socialListening: 'Social listening',
    realTimeMentionFeedAndSocial: 'Real-time mention feed and social lead detection from your keywords and competitors.',
    monitoringListening: 'Monitoring / Listening',
    brandKeywordCompetitorMonitorsAggregated: 'Brand / keyword / competitor monitors, aggregated signals (volume, sentiment, topics) and alerts — with honest sourcing.',
    socialLeads: 'Social leads',
    spotAndQualifyProspectsComingFrom: 'Spot and qualify prospects coming from social networks, ready to move to the CRM.',
    influencerCampaigns: 'Influencer campaigns',
    influencerShortlistBoardByStageAnd: 'Influencer shortlist, board by stage and impact measurement, from first contact to ROI.',
    ambassadorsAdvocacy: 'Ambassadors (advocacy)',
    shareFeedForYourEmployeesTracked: 'Share feed for your employees, tracked sharing with consent and a leaderboard — amplify your organic reach.',
    offersProspecting: 'Offers & Prospecting',
    describeEachProductBrandKingdomConnectio: 'Describe each product/brand (Kingdom Connection, Kimmobilia…) with its ideal customer (ICP), then prospect: targeted results imported into the CRM, tagged by offer.',
    b2bDiscovery: 'B2B discovery',
    liveB2bSearchEmailPhoneEnrichment: 'Live B2B search, email/phone enrichment, intent signals and lookalikes.',
    prospectingStrategy: 'Prospecting strategy',
    theAiPlanThatPrecedesThe: 'The AI plan that precedes the calendar: targets, messages and sequences — step 1 of the funnel.',
    recommendedProspects: 'Recommended prospects',
    theAiRanksYourProspectsBy: 'The AI ranks your prospects by fit with the objective and the product — tackle the most promising first.',
    accountIntelligence: 'Account intelligence',
    aiDossierPerCompanyNewsSignals: 'AI dossier per company: news, signals and a personalized approach angle for each key account.',
    prospectingCalendar: 'Prospecting calendar',
    datedMultiTouchSequenceEachTouchpoint: 'Dated multi-touch sequence: each touchpoint scheduled on the right channel, at the right time.',
    identifiedVisitors: 'Identified visitors',
    identifyTheCompaniesVisitingYourSite: 'Identify the companies visiting your site and turn anonymous traffic into actionable leads.',
    jobChangeAlerts: 'Job-change alerts',
    getAlertedWhenAContactChanges: 'Get alerted when a contact changes jobs — the best time to reconnect and sell.',
    inboundLeadRouting: 'Inbound lead routing',
    automaticallyAssignEachInboundLeadTo: 'Automatically assign each inbound lead to the right salesperson according to your rules.',
    crmLeads: 'CRM & Leads',
    contactsCompaniesKanbanPipelineScoringIn: 'Contacts, companies, kanban pipeline, scoring (including PQL), follow-ups and activity timeline.',
    salesPipeline: 'Sales pipeline',
    kanbanViewOfYourOpportunitiesBy: 'Kanban view of your opportunities by stage: drag and drop, forecasts and live weighted value.',
    intentSignals: 'Intent signals',
    aggregateBuyingSignalsVisitsOpensEngagem: 'Aggregate buying signals (visits, opens, engagements) to act at the precise moment of interest.',
    emailSms: 'Email & SMS',
    trackedMultiStepSequencesEmailSms: 'Tracked multi-step sequences (email + SMS), campaigns, templates, deliverability — inbound STOP and CASL/Law-25 consent portal.',
    uniboxReplies: 'Unibox (replies)',
    allRepliesFromYourEmailSms: 'All replies from your email/SMS sequences gathered in a single inbox, so you never miss an opportunity.',
    servicesMarketplace: 'Services marketplace',
    providerDirectoryBookingsAndReviewsFind: 'Provider directory, bookings and reviews — find the expert who completes your team.',
    createAnAdWithAi: 'Create an ad with AI',
    pasteAProductUrlTheAi: 'Paste a product URL → the AI writes the copy and generates the image/video → a PAUSED ad draft to review before it runs. (the “fast creative→ad loop”)',
    ads: 'Ads',
    multiPlatformCampaignsABAuto: 'Multi-platform campaigns, A/B + auto winner, audiences & lookalikes, pixel/CAPI (consent), anti-fatigue rules, Meta review status and live editing of targeting/budget — all from the portal.',
    adsCommandCenter: 'Ads command center',
    fullControlInOnePlaceEngagement: 'Full control in one place: engagement per ad, creative preview, billing, ad calendar, AI planner and online updating of published campaigns.',
    analyticsAi: 'Analytics & AI',
    performanceConversionFunnelsMultiTouchAt: 'Performance, conversion funnels, multi-touch attribution (ROI), AI insights, PQL score and “next best action” recommendations.',
    reportsBenchmark: 'Reports & Benchmark',
    whiteLabelClientReadyReportsAnd: 'White-label client-ready reports and competitive benchmark — exportable and schedulable.',
    geoAiVisibility: 'GEO · AI visibility',
    measureYourPresenceInAiSearch: 'Measure your presence in AI search engines (ChatGPT, Perplexity…) and optimize to appear there.',
    attributionRoi: 'Attribution / ROI',
    multiTouchAttributionOfConversionTo: 'Multi-touch attribution of conversion to its touchpoints — know which channel really generates revenue.',
    monitoringAlerts: 'Monitoring & alerts',
    monitorYourKpisContinuouslyAndGet: 'Monitor your KPIs continuously and get an alert as soon as a metric drops.',
    aiAssistantCopilot: 'AI assistant (Copilot)',
    driveEverythingInNaturalLanguageCreate: 'Drive everything in natural language: “create an AI ad for Kingdom Connection”, “prospect for Kimmobilia and launch a sequence” — 50+ growth tools, draft first, compliant.',
    n247AiTeamVirtualExperts: '24/7 AI team (virtual experts)',
    autonomousMultiAgentSystemPlansAnd: 'Autonomous multi-agent system: plans and executes your growth actions (ads, prospecting, funnel, nurture) under guardrails and compliance.',
    aiTeamHub: 'AI team — Hub',
    theEntryPointForYourVirtual: 'The entry point for your virtual experts: enable each persona and launch the orchestrator\'s agents.',
    virtualOffice: 'Virtual office',
    openSpaceViewOfYourAi: '“Open space” view of your AI team at work: who is doing what, live.',
    aiTeams: 'AI teams',
    composeAndManageSeveralAiTeams: 'Compose and manage several AI teams, each dedicated to a goal (content, prospecting, ads).',
    teamBuilder: 'Team builder',
    buildATeamSeatBySeat: 'Build a team seat by seat: role × count, skills, tools and autonomy level per position.',
    teamTemplates: 'Team templates',
    startFromAReusableCompositionTemplate: 'Start from a reusable composition template to deploy a ready-to-use team in one click.',
    taskQueue: 'Task queue',
    theSharedTaskPoolPrioritizeAnd: 'The shared task pool: prioritize and distribute work among your agents.',
    deliverables: 'Deliverables',
    retrieveAndApproveTheDeliverablesProduce: 'Retrieve and approve the deliverables produced by the AI team (posts, ads, lists, reports).',
    schedules: 'Schedules',
    defineEachAgentSActivityWindows: 'Define each agent\'s activity windows — a truly 24/7 team or one aligned with your hours.',
    controlConsole: 'Control console',
    masterSwitchMandateCadenceAndBudget: 'Master switch, mandate, cadence and budget of the coordinator, autonomy per persona — all under guardrails.',
    aiAuditLog: 'AI audit log',
    fullTraceabilityEveryAiDecisionAnd: 'Full traceability: every AI decision and action is logged, reviewable and exportable.',
    multiAgentOrchestration: 'Multi-agent orchestration',
    fromAGoalTheAiBuilds: 'From a goal, the AI builds a multi-persona graph plan, then executes it live, step by step.',
    aiApprovals: 'AI approvals',
    queueOfAiProposedActionsAwaiting: 'Queue of AI-proposed actions awaiting your go-ahead before execution.',
    aiTeamQuestions: 'AI team questions',
    whenAnAgentNeedsAClarification: 'When an agent needs a clarification, it asks you here instead of improvising.',
    accessRequests: 'Access requests',
    agentsExplicitlyRequestAdditionalAccessO: 'Agents explicitly request additional access or permission, which you grant or deny.',
    aiActivity: 'AI activity',
    theRealTimeStreamOfEverything: 'The real-time stream of everything the AI does: full transparency on every operation.',
    aiContentStudio: 'AI content studio',
    postGenerationGuidedByYourBrand: 'Post generation guided by your brand voice, with hooks, variants and a performance score.',
    ghostwriter: 'Ghostwriter',
    writeInYourStyleFromA: 'Write in your style from a few key points — the AI imitates your tone, not a generic one.',
    contentReuse: 'Content reuse',
    turnAnArticleAVideoOr: 'Turn an article, a video or a post into variants ready for each network.',
    hookLibrary: 'Hook library',
    hundredsOfProvenHooksFilterableBy: 'Hundreds of proven hooks, filterable by goal, to grab attention from the first line.',
    longFormStudio: 'Long-Form Studio',
    writeBlogArticlesNewslettersAndStructure: 'Write blog articles, newsletters and structured long pages, from outline to final version.',
    canvas: 'Canvas',
    aFreeFormWritingSpaceSide: 'A free-form writing space side by side with the AI to iterate deeply on a text.',
    gridBulkGeneration: 'Grid · bulk generation',
    generateDozensOfVariantsInBulk: 'Generate dozens of variants in bulk in a grid and pick the best ones at once.',
    brandVoice: 'Brand voice',
    defineAndTrainYourBrandVoice: 'Define and train your brand voice so all generations stay consistent.',
    aBCopy: 'A/B copy',
    produceSeveralVersionsOfAText: 'Produce several versions of a text to test and let the data pick the winner.',
    aiSuggestions: 'AI suggestions',
    contextualContentAndOptimizationRecommen: 'Contextual content and optimization recommendations as you work.',
    personalization: 'Personalization',
    automaticallyAdaptMessageAndToneTo: 'Automatically adapt message and tone to each audience segment or recipient.',
    communityAi: 'Community AI',
    theAiDraftsRepliesToComments: 'The AI drafts replies to comments and messages in your tone, ready to approve.',
    ragDocuments: 'RAG documents',
    feedTheAiYourOwnDocuments: 'Feed the AI your own documents: it answers and creates based on your knowledge base.',
    aiMemory: 'AI memory',
    persistentMemoryThatKeepsYourContext: 'Persistent memory that keeps your context, preferences and key facts from one session to the next.',
    workflowsRpa: 'Workflows & RPA',
    automateEndToEndReadyMade: 'Automate end to end. Ready-made templates: Prospect→Import→Nurture, AI ad on new product, Funnel capture→Nurture, Post-purchase review follow-up.',
    flowStudio: 'Flow studio',
    dragAndDropAgenticEditorBpmn: 'Drag-and-drop agentic editor (BPMN import, DMN decisions) with live execution visualization.',
    automations: 'Automations',
    conditionalPostPublicationRulesDisabledB: 'Conditional post-publication rules, disabled by default and enabled only after a successful dry run.',
    aiLearning: 'AI learning',
    whatWorksPerPlatformConfirmedWinning: '“What works per platform”: confirmed winning patterns, opt-in and erasure, and transposition of a high-performing post.',
    profile: 'Profile',
    yourPersonalInformationLanguageAndDispla: 'Your personal information, language and display preferences.',
    teamInvitations: 'Team & invitations',
    inviteColleaguesAssignRolesAndManage: 'Invite colleagues, assign roles and manage your organization\'s members.',
    organization: 'Organization',
    organizationIdentityNameDomainPlanAnd: 'Organization identity: name, domain, plan and quotas.',
    notifications: 'Notifications',
    choosePreciselyWhenAndHowTo: 'Choose precisely when and how to be notified (email, in-app, digests).',
    rolesPermissions: 'Roles & permissions',
    createCustomRolesAndAPermission: 'Create custom roles and a permission matrix per resource — everyone only sees what concerns them.',
    accessDelegationManagedAccounts: 'Access delegation (Managed accounts)',
    grantAnExternalManagerVeryGranular: 'Grant an external manager very granular rights — e.g. publish on Facebook but not LinkedIn, or only create ads: the person keeps their own account and only sees what is granted. Every access is logged and revocable, and the “Managed accounts” switcher lets them move between the accounts entrusted to them.',
    networkConnections: 'Network connections',
    linkAndReconnectYourSocialAccounts: 'Link and reconnect your social accounts; expired tokens renew automatically.',
    connectors: 'Connectors',
    configureTheDataConnectorsAndThird: 'Configure the data connectors and third-party services that power the platform.',
    integrations: 'Integrations',
    plugInYourToolsCrmSlack: 'Plug in your tools (CRM, Slack, Teams, Zapier…) to sync data and notifications.',
    apiDevelopers: 'API & developers',
    apiKeysOauth2AppsAndWebhooks: 'API keys, OAuth2 apps and webhooks to build your own integrations.',
    sendingMailboxes: 'Sending mailboxes',
    smtpSendingMailboxesAndDomainPurchase: 'SMTP sending mailboxes and domain purchase for controlled email deliverability.',
    subscriptionPaywall: 'Subscription & Paywall',
    plansCapsAiCreditsReverseTrial: 'Plans, caps, AI credits, reverse trial and a contextual “X/Y used → upgrade” paywall.',
    billing: 'Billing',
    yourAiCreditsPurchasesAndBilling: 'Your AI credits, purchases and billing history in one place.',
    aiCredits: 'AI credits',
    trackAiCreditConsumptionPerFeature: 'Track AI credit consumption per feature and top up on demand.',
    perUserBudgets: 'Per-user budgets',
    setAiCreditCapsPerUser: 'Set AI credit caps per user to control team costs.',
    referralAffiliation: 'Referral & Affiliation',
    twoSidedReferralAffiliateProgramTracked: 'Two-sided referral, affiliate program (tracked links, commissions, dashboard) and a “Made with Karexia” badge.',
    aiRegistry: 'AI registry',
    agentsSkillsPromptsModelsAndTools: 'Agents, skills, prompts, models and tools — the platform\'s AI brain.',
    aiModelsAdmin: 'AI models (admin)',
    chooseTheAvailableModelsTheirCredit: 'Choose the available models, their credit cost and the default model per task (Auto mode).',
    llmKeysByok: 'LLM keys (BYOK)',
    bringYourOwnLlmKeysBill: 'Bring your own LLM keys: bill the AI to your provider account rather than to your credits.',
    aiGovernance: 'AI governance',
    guardrailsAutonomyLimitsAndAiUsage: 'Guardrails, autonomy limits and AI usage policies across the organization.',
    contentEngine: 'Content engine',
    contentAutomationPolicyEverythingOffBy: 'Content automation policy: everything off by default, with a Law-25 notice and erasure of learning data.',
    reviewPolicy: 'Review policy',
    autoPublishingMasterSwitchRulesAnd: 'Auto-publishing master switch, rules and simulation — manual mode by default, Law-25 compliant.',
    trustCenter: 'Trust center',
    consentRegisterStopUnsubscribeListsPer: 'Consent register, STOP/unsubscribe lists, per-regime config and data erasure — Law-25/CASL compliance.',
    recV: '● REC {{v}}',
    stopDownload: 'Stop & download',
    closeTheMessage: 'Close the message',
    demoOptions: 'Demo options',
    startTheGuidedDemo: '🎬 Start the guided demo',
    tipInTheBrowserSSharing: 'Tip: in the browser\'s sharing window, choose “This tab” for a clean capture.',
    recordTheDemoAsAVideo: '🎥 Record the demo as a video',
    recordASingleFeature9S: 'Record a single feature (~9 s)',
    rec: '● REC',
    startTheGuidedDemo2: 'Start the guided demo',
    guidedDemo: '🎬 Guided demo',
    guidedDemo2: 'Guided demo',
    demoVLength: 'Demo · {{v}}/{{length}}',
    closeTheDemo: 'Close the demo',
    recordingInProgress: '● Recording in progress',
    autoplay: 'Autoplay',
    previous: 'Previous',
    finish: 'Finish',
    next: 'Next',
  },
  dialerCallLog: {
    couldNotLoadTheCallLog: 'Could not load the call log.',
    loading: 'Loading…',
    theCallLogIsNotYet: 'The call log is not yet available on this environment.',
    noCall: 'No call.',
    noCallRecordedYet: 'No call recorded yet.',
    recording: '🎧 Recording',
    logTheOutcome: 'Log the outcome',
    callOutcome: 'Call outcome',
  },
  dialerDialerModal: {
    callsNotConfiguredOnThisEnvironment: 'Calls not configured on this environment.',
    couldNotStartTheCall: 'Could not start the call.',
    callContactname: 'Call {{contactName}}',
    newCall: 'New call',
    numberToCall: 'Number to call',
    noNumberSavedEnterOneTo: 'No number saved — enter one to call.',
    yourNumberTheAgentSPhone: 'Your number (the agent\'s phone)',
    weCallThisNumberFirstThen: 'We call this number first, then connect it to the prospect.',
    call: '📞 Call',
    connectingToTo: 'Connecting to {{to}}…',
    outcomeSaved: 'Outcome saved',
    toToNumber: 'To {{to_number}}',
    callBridgedOnTheServerAny: 'Call bridged on the server. Any recording is subject to consent and handled on the telephony side.',
    close: 'Close',
  },
  dialerEmailCallButton: {
    call: '📞 Call',
    callPhone: 'Call {{phone}}',
    callEnterTheNumberManually: 'Call (enter the number manually)',
  },
  dialerOutcomeForm: {
    savingTheOutcomeFailed: 'Saving the outcome failed.',
    result: 'Result',
    durationMinutesOptional: 'Duration (minutes, optional)',
    notes: 'Notes',
    keyPointsNextStep: 'Key points, next step…',
    saveTheOutcome: 'Save the outcome',
    cancel: 'Cancel',
  },
  entitlementsPlanGate: {
    featureNotIncludedInYourPlan: 'Feature not included in your plan',
    labelIsNotIncludedInYour: '“{{label}}” is not included in your plan',
    upgradeToAHigherPlanTo: 'Upgrade to a higher plan to unlock this feature.',
    viewThePlans: 'View the plans',
    upgradeYourPlan: 'Upgrade your plan',
    later: 'Later',
    theFeature: 'The feature',
    requiresAHigherPlan: 'requires a higher plan.',
    chooseThePlanThatUnlocksLabel: 'Choose the plan that unlocks “{{label}}” and the other advanced features.',
    adminAllFeatures: 'Admin: all features',
    adminAllFeaturesHint: 'Platform admin — every plan feature is unlocked and every limit is unlimited.',
  },
  entitlementsPlanRequiredHost: {
    featureNotIncludedInYourPlan: 'Feature not included in your plan',
    later: 'Later',
    viewThePlans: 'View the plans',
    isNotIncludedInYourPlan: '” is not included in your plan',
    upgradeToAHigherPlanTo: 'Upgrade to a higher plan to unlock this feature.',
  },
  strategyStrategyBrief: {
    sentiment: 'Sentiment',
    commentThemes: 'Comment themes',
    postMetrics: 'Post metrics',
    bestTimeToPost: 'Best time to post',
    ads: 'Ads',
    upToDate: 'up to date',
    latestData: 'Latest data:',
    applyTheRecommendation: 'Apply the recommendation',
    whyThisActionGoesThroughThe: '{{why}}\n\nThis action goes through the automation filter (compliance + thresholds). Continue?',
    apply: 'Apply',
    actionAppliedViaTheFilter: 'Action applied (via the filter).',
    estimatedImpactVV2V3V4: 'Estimated impact: {{v}}–{{v2}}{{v3}}{{v4}}',
    confidenceV: 'Confidence {{v}}%',
    applied: '✓ Applied',
    dismissed: '✕ Dismissed',
    applying: 'Applying…',
    dismiss: 'Dismiss',
    whyOptionalHelpsTheAiImprove: 'Why? (optional — helps the AI improve)',
    sending: 'Sending…',
    confirm: 'Confirm',
    weeklyStrategicBrief: 'Weekly strategic brief',
    strategicBriefWeekWeek: 'Strategic brief — week {{week}}',
    previousWeek: 'Previous week',
    thisWeek: 'This week',
    nextWeek: 'Next week',
    analyzingTheCurrentWeek: 'Analyzing the current week…',
    theStrategicBriefWillBeAvailable: 'The strategic brief will be available soon.',
    couldNotLoadTheBriefMessage: 'Could not load the brief: {{message}}',
    notEnoughDataYetForA: 'Not enough data yet for a reliable brief. Publish a few posts and launch a campaign: the AI will analyze your results and suggest a strategy.',
    analyzedSignals: 'Analyzed signals',
    recommendedActionsLength: 'Recommended actions ({{length}})',
    noActionThisWeekKeepUp: 'No action this week — keep up the momentum.',
    missingSignals: 'Missing signals:',
    theseRecommendationsAreThereforePartial: '. These recommendations are therefore partial.',
    unavailable: 'unavailable',
  },
  supportSupportPanel: {
    filenameVKb: '📎 {{filename}} ({{v}} KB)',
  },
  telephonyCallButton: {
    callPhone: 'Call {{phone}}',
    callNoNumberSavedManualEntry: 'Call (no number saved — manual entry)',
    call: '📞 Call',
  },
  telephonyCallInsightsPanel: {
    callAnalysisNotConfiguredOnThis: 'Call analysis not configured on this environment.',
    positive: 'Positive',
    negative: 'Negative',
    neutral: 'Neutral',
    mixed: 'Mixed',
    couldNotAnalyzeThisCall: 'Could not analyze this call.',
    aiCallAnalysis: 'AI call analysis',
    recordingUrlOptional: 'Recording URL (optional)',
    callRecordingUrl: 'Call recording URL',
    orPasteTheCallTranscript: 'Or paste the call transcript…',
    callTranscript: 'Call transcript',
    analyzing: 'Analyzing…',
    analyzeTheCall: '🧠 Analyze the call',
    sentimentSentiment: 'Sentiment: {{sentiment}}',
    newAnalysis: 'New analysis',
    summary: 'Summary',
    actionItems: 'Action items',
    noActionIdentified: 'No action identified.',
    transcription: 'Transcription',
  },
  telephonyDialer: {
    telephonyNotConfiguredOnThisEnvironment: 'Telephony not configured on this environment.',
    queued: 'Queued…',
    initiated: 'Initiated…',
    ringing: 'Ringing…',
    inCall: 'In call',
    done: 'Done',
    busy: 'Busy',
    callFailed: 'Call failed',
    unanswered: 'Unanswered',
    cancelled: 'Cancelled',
    couldNotCheckTelephony: 'Could not check telephony.',
    couldNotStartTheCall: 'Could not start the call.',
    callContactname: 'Call {{contactName}}',
    checkingTelephony: 'Checking telephony…',
    lineReadyOutboundCallFromIdentity: 'Line ready — outbound call from {{identity}}.',
    hideThePreCallBrief: 'Hide the pre-call brief',
    viewThePreCallBrief: '📋 View the pre-call brief',
    numberToCall: 'Number to call',
    noNumberSavedForThisContact: 'No number saved for this contact — enter one to call.',
    call: '📞 Call',
    connectingToTo: 'Connecting to {{to}}…',
    callEnded: 'Call ended',
    toToSidCallSid: 'To {{to}} · SID {{call_sid}}',
    muteUnavailableInServerCallMode: 'Mute unavailable in server call mode (no browser audio stream to mute — the call is bridged on the telephony side, not in this browser).',
    muteUnavailable: '🔇 Mute (unavailable)',
    muteUnavailable2: '🎙️ Mute (unavailable)',
    endCloseTheTracker: '⏹️ End (close the tracker)',
    theStatusAboveIsTheOne: 'The status above is the one returned when the call was started — this window does not receive live updates. Actually end the call from the bridged phone; the “End” button only closes this on-screen tracker.',
  },
  telephonyPreMeetingBrief: {
    preCallBriefNotConfiguredOn: 'Pre-call brief not configured on this environment.',
    couldNotGenerateThePreCall: 'Could not generate the pre-call brief.',
    generating: 'Generating…',
    generateThePreCallBrief: '📋 Generate the pre-call brief',
    preCallBrief: 'Pre-call brief',
    refreshing: 'Refreshing…',
    refresh: 'Refresh',
    company: 'Company',
    recentSignals: 'Recent signals',
    noRecentSignalDetected: 'No recent signal detected.',
    talkingPoints: 'Talking points',
    noTalkingPointSuggested: 'No talking point suggested.',
    lastCallSummary: 'Last call summary',
  },
};

export default en;
